import { defineComponent as _defineComponent } from 'vue'
import { onMounted, ref, computed, PropType, watch } from "vue";
  import { useToast } from "vue-toastification";
  import { useStore } from "vuex";
  import { AnalyticsDataInterface } from "@/types/AnalyticsDataInterface";
  import { AnalyticsDataItem } from "@/types/AnalyticsDataInterface";
  import { EstateInterface } from "@/types/EstateInterface";
  import { LeadInterface } from "@/types/LeadInterface";
  import LeadSourceEnum from "@/enums/LeadSourceEnum";
  import ReportsByNumber from "@/components/reports/ReportsByNumber.vue";
  import { ReportsTimePeriod } from "@/enums/ReportsTimePeriod";
  import { ReportsTitleByNumber } from "@/enums/ReportsTitleByNumber";
  import { calculateDateRange } from "@/helpers/calculateDateRange";

  
export default /*@__PURE__*/_defineComponent({
  __name: 'ConversionFromAnalytics',
  props: {
    estate: {
      type: Object as () => EstateInterface,
      required: true,
    },
    period: {
      type: String as PropType<ReportsTimePeriod>,
      required: true,
    },
  },
  setup(__props, { expose: __expose }) {
  __expose();

  const props = __props;

  const leadsSources = [LeadSourceEnum.ENQUIRY, LeadSourceEnum.INITIAL_HOLD];
  const leadsSourcesAsString = leadsSources.map((source) => source.toString());

  const loading = ref(false);
  const toast = useToast();
  const store = useStore();
  const DataFromAnalytics = ref<AnalyticsDataItem[]>([]);
  const estate = ref<EstateInterface>();
  const filteredLeads = ref<LeadInterface[]>([]);
  const filteredLeadsCounts = ref(0);
  const estateSelected = computed(() => {
    return props.estate;
  });
  const periodSelected = computed(() => {
    return props.period;
  });

  watch(periodSelected, (newPeriod, oldPeriod) => {
    if (newPeriod !== oldPeriod) {
      fetchAnalyticsData();
      getLeadsByEstate();
    }
  });

  const DataForAnalytics: AnalyticsDataInterface = {
    dateRanges: [{ startDate: "30daysAgo", endDate: "yesterday" }],
    metrics: [{ name: "sessions" }],
    dimensions: [{ name: "pageTitle" }],
    orderBys: [
      {
        dimension: { orderType: "ALPHANUMERIC", dimensionName: "pageTitle" },
        desc: false,
      },
    ],
    keepEmptyRows: true,
  };

  const fetchAnalyticsData = () => {
    loading.value = true;

    const { startDate, endDate } = calculateDateRange(periodSelected.value);

    DataForAnalytics.dateRanges = [{ startDate, endDate }];

    store
      .dispatch("analytics/createReport", DataForAnalytics)
      .then((res) => {
        DataFromAnalytics.value = res.data[0].rows;
        loading.value = false;
      })
      .catch((error) => {
        toast.error("Error getting data from Google Analytics");
      });
  };

  const getEstate = () => {
    if (estateSelected.value) {
      store
        .dispatch("estate/getEstate", estateSelected.value._id)
        .then((res) => {
          estate.value = res.data;
          getLeadsByEstate();
        })
        .catch((error) => {
          toast.error("Error getting estates");
        });
    }
  };

  const filterLeadsByPeriod = (
    leads: LeadInterface[],
    period: ReportsTimePeriod
  ) => {
    const startDate = new Date();

    if (period === ReportsTimePeriod.LAST_WEEK) {
      startDate.setDate(startDate.getDate() - 7);
    } else if (period === ReportsTimePeriod.LAST_MONTH) {
      startDate.setMonth(startDate.getMonth() - 1);
      startDate.setDate(1);
    }

    return leads.filter((lead) => {
      const createdAt = new Date(lead.createdAt);

      return createdAt >= startDate;
    });
  };

  const getLeadsByEstate = () => {
    if (estateSelected.value) {
      store
        .dispatch("contact/getLeads", {
          estateId: estateSelected.value._id,
        })
        .then((res) => {
          const leadsWithinPeriod = filterLeadsByPeriod(
            res.data,
            periodSelected.value
          );

          filteredLeads.value = leadsWithinPeriod.filter(
            (lead: LeadInterface) => {
              return leadsSourcesAsString.includes(lead.source);
            }
          );
          filteredLeadsCounts.value = filteredLeads.value.length;
        })
        .catch((error) => {
          toast.error("Error getting Leads by estate");
        });
    }
  };

  const filteredDataFromAnalytics = computed(() => {
    if (estate.value && DataFromAnalytics.value.length > 0) {
      const estateTitle = estate.value.title;

      return DataFromAnalytics.value.filter(
        (estateByAnalytics) =>
          estateByAnalytics.dimensionValues[0].value === estateTitle
      );
    }

    return [];
  });

  const ReportFromAnalyticsNumber = computed(() => {
    if (!filteredDataFromAnalytics.value.length) {
      return 0;
    }

    return parseInt(filteredDataFromAnalytics?.value[0].metricValues[0].value);
  });

  const conversionRate = computed(() => {
    const visitors = ReportFromAnalyticsNumber.value;
    const leads = filteredLeadsCounts.value;

    if (visitors === 0) {
      return 0;
    }

    const rate = (leads / visitors) * 100;

    return rate.toFixed(2);
  });

  const conversionRateOrZero = computed(() => {
    return conversionRate.value || 0;
  });

  onMounted(() => {
    fetchAnalyticsData();
    getEstate();
  });

const __returned__ = { props, leadsSources, leadsSourcesAsString, loading, toast, store, DataFromAnalytics, estate, filteredLeads, filteredLeadsCounts, estateSelected, periodSelected, DataForAnalytics, fetchAnalyticsData, getEstate, filterLeadsByPeriod, getLeadsByEstate, filteredDataFromAnalytics, ReportFromAnalyticsNumber, conversionRate, conversionRateOrZero, onMounted, ref, computed, PropType, watch, get useToast() { return useToast }, get useStore() { return useStore }, get AnalyticsDataInterface() { return AnalyticsDataInterface }, get AnalyticsDataItem() { return AnalyticsDataItem }, get EstateInterface() { return EstateInterface }, get LeadInterface() { return LeadInterface }, get LeadSourceEnum() { return LeadSourceEnum }, ReportsByNumber, get ReportsTimePeriod() { return ReportsTimePeriod }, get ReportsTitleByNumber() { return ReportsTitleByNumber }, get calculateDateRange() { return calculateDateRange } }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})